import { Deal } from '../types';

// PBN Export Functions
export const exportToPBN = (deals: Deal[], eventName: string = 'Bridge Deals'): string => {
  let pbnContent = `% Bridge Deal Generator Export\n`;
  pbnContent += `[Event "${eventName}"]\n`;
  pbnContent += `[Site "Generated"]\n`;
  pbnContent += `[Date "${new Date().toISOString().split('T')[0]}"]\n`;
  pbnContent += `[Scoring "MP"]\n\n`;

  deals.forEach((deal, index) => {
    const boardNumber = index + 1;
    pbnContent += `[Board "${boardNumber}"]\n`;
    pbnContent += `[Dealer "${deal.dealer.charAt(0)}"]\n`;
    pbnContent += `[Vulnerable "${deal.vulnerability}"]\n`;
    
    // Format hands for PBN
    const northHand = formatHandForPBN(deal.north.cards);
    const eastHand = formatHandForPBN(deal.east.cards);
    const southHand = formatHandForPBN(deal.south.cards);
    const westHand = formatHandForPBN(deal.west.cards);
    
    // Order hands starting from dealer position
    const hands = [northHand, eastHand, southHand, westHand];
    const dealerIndex = ['North', 'East', 'South', 'West'].indexOf(deal.dealer);
    const orderedHands = [
      hands[dealerIndex],
      hands[(dealerIndex + 1) % 4],
      hands[(dealerIndex + 2) % 4],
      hands[(dealerIndex + 3) % 4]
    ];
    
    pbnContent += `[Deal "${deal.dealer.charAt(0)}:${orderedHands.join(' ')}"]\n\n`;
  });

  return pbnContent;
};

const formatHandForPBN = (cards: any[]): string => {
  const suits = ['S', 'H', 'D', 'C'];
  let handStr = '';
  
  suits.forEach(suit => {
    const suitCards = cards
      .filter(card => card.suit === suit)
      .sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank))
      .map(card => card.rank)
      .join('');
    
    if (suitCards) {
      handStr += suitCards + '.';
    } else {
      handStr += '.';
    }
  });
  
  return handStr.slice(0, -1); // Remove trailing dot
};

const getRankValue = (rank: string): number => {
  const rankValues: { [key: string]: number } = {
    'A': 14, 'K': 13, 'Q': 12, 'J': 11, 'T': 10,
    '9': 9, '8': 8, '7': 7, '6': 6, '5': 5, '4': 4, '3': 3, '2': 2
  };
  return rankValues[rank] || 0;
};

// BRI Export Functions
export const exportToBRI = (deals: Deal[]): Uint8Array => {
  const recordSize = 128;
  const totalSize = deals.length * recordSize;
  const buffer = new Uint8Array(totalSize);
  
  deals.forEach((deal, index) => {
    const offset = index * recordSize;
    const record = createBRIRecord(deal);
    buffer.set(record, offset);
  });
  
  return buffer;
};

const createBRIRecord = (deal: Deal): Uint8Array => {
  const record = new Uint8Array(128);
  
  // Convert hands to BRI format
  const northCards = handToBRICards(deal.north.cards);
  const eastCards = handToBRICards(deal.east.cards);
  const southCards = handToBRICards(deal.south.cards);
  const westCards = handToBRICards(deal.west.cards);
  
  // Order hands starting from dealer position
  const hands = [northCards, eastCards, southCards, westCards];
  const dealerIndex = ['North', 'East', 'South', 'West'].indexOf(deal.dealer);
  const orderedHands = [
    hands[dealerIndex],
    hands[(dealerIndex + 1) % 4],
    hands[(dealerIndex + 2) % 4],
    hands[(dealerIndex + 3) % 4]
  ];
  
  // Combine all cards (first 78 bytes) in dealer-first order
  const allCards = [...orderedHands[0], ...orderedHands[1], ...orderedHands[2], ...orderedHands[3]];
  for (let i = 0; i < 78; i++) {
    record[i] = allCards[i] || 0x20; // Space if no card
  }
  
  // Add 32 space characters (bytes 78-109)
  for (let i = 78; i < 110; i++) {
    record[i] = 0x20;
  }
  
  // Add 18 null characters (bytes 110-127)
  for (let i = 110; i < 128; i++) {
    record[i] = 0x00;
  }
  
  return record;
};

const handToBRICards = (cards: any[]): number[] => {
  const cardMap: { [key: string]: number } = {
    // Spades: 01-13
    'AS': 1, 'KS': 2, 'QS': 3, 'JS': 4, 'TS': 5, '9S': 6, '8S': 7, '7S': 8, '6S': 9, '5S': 10, '4S': 11, '3S': 12, '2S': 13,
    // Hearts: 14-26
    'AH': 14, 'KH': 15, 'QH': 16, 'JH': 17, 'TH': 18, '9H': 19, '8H': 20, '7H': 21, '6H': 22, '5H': 23, '4H': 24, '3H': 25, '2H': 26,
    // Diamonds: 27-39
    'AD': 27, 'KD': 28, 'QD': 29, 'JD': 30, 'TD': 31, '9D': 32, '8D': 33, '7D': 34, '6D': 35, '5D': 36, '4D': 37, '3D': 38, '2D': 39,
    // Clubs: 40-52
    'AC': 40, 'KC': 41, 'QC': 42, 'JC': 43, 'TC': 44, '9C': 45, '8C': 46, '7C': 47, '6C': 48, '5C': 49, '4C': 50, '3C': 51, '2C': 52
  };
  
  return cards.map(card => {
    const cardKey = card.rank + card.suit;
    return cardMap[cardKey] || 0;
  }).sort((a, b) => a - b);
};

// HTML Export for Printing — compact 3 across × 7 down (21 boards per page)
export const exportToHTML = (deals: Deal[]): string => {
  const cols = 3;
  const rows = 7;
  const boardsPerPage = cols * rows;
  const pages: Deal[][] = [];
  for (let i = 0; i < deals.length; i += boardsPerPage) {
    pages.push(deals.slice(i, i + boardsPerPage));
  }

  let html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Bridge Deals</title>
    <style>
        * { box-sizing: border-box; }
        body {
            font-family: Arial, Helvetica, sans-serif;
            margin: 0;
            padding: 4px;
            color: #111;
            font-size: 9px;
        }
        .doc-header {
            margin: 0 0 4px 0;
            padding-bottom: 2px;
            border-bottom: 1px solid #ccc;
        }
        .doc-header h1 {
            margin: 0;
            font-size: 11px;
        }
        .doc-header p {
            margin: 1px 0 0 0;
            font-size: 8px;
            color: #555;
        }
        .page {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-auto-rows: auto;
            gap: 3px;
            align-content: start;
            page-break-after: always;
            break-after: page;
        }
        .page:last-child {
            page-break-after: auto;
            break-after: auto;
        }
        .deal {
            border: 1px solid #999;
            padding: 2px 3px 3px;
            display: flex;
            flex-direction: column;
            gap: 1px;
            overflow: hidden;
        }
        .deal-header {
            font-weight: bold;
            font-size: 7.5px;
            line-height: 1.1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .deal-header .vuln { color: #c00; }
        .table {
            display: grid;
            grid-template-areas:
                ". north ."
                "west . east"
                ". south .";
            grid-template-columns: max-content max-content max-content;
            grid-template-rows: max-content max-content max-content;
            gap: 0 2px;
            justify-content: center;
            align-items: start;
            width: 100%;
        }
        .pos { text-align: left; }
        .pos.north { grid-area: north; }
        .pos.south { grid-area: south; }
        .pos.east { grid-area: east; }
        .pos.west { grid-area: west; }
        .pos-label {
            font-weight: bold;
            font-size: 7px;
            line-height: 1;
            margin: 0;
        }
        .cards {
            font-family: "Courier New", Courier, monospace;
            font-size: 8px;
            line-height: 1.05;
            text-align: left;
        }
        .suit-line { white-space: nowrap; }
        .suit-line.red { color: #c00; }
        .suit-line.black { color: #111; }
        .suit-sym { display: inline-block; width: 0.85em; font-weight: bold; }
        @media print {
            body { padding: 3mm; }
            .page { gap: 2px; }
            .deal { break-inside: avoid; page-break-inside: avoid; }
            .doc-header { display: none; }
        }
        @page {
            size: A4 portrait;
            margin: 5mm;
        }
    </style>
</head>
<body>
    <div class="doc-header">
        <h1>Bridge Deals</h1>
        <p>Generated on: ${new Date().toLocaleString()} · ${deals.length} boards · 3×7 per page</p>
    </div>
`;

  pages.forEach((pageDeals) => {
    html += `    <div class="page">\n`;
    pageDeals.forEach((deal) => {
      html += `        <div class="deal">
            <div class="deal-header">
                Bd ${deal.dealNumber} · Dlr ${deal.dealer.charAt(0)} ·
                <span class="vuln">${formatVulnShort(deal.vulnerability)}</span> ·
                Op ${deal.openingHand.charAt(0)} · ${escapeHtml(deal.category)}
            </div>
            <div class="table">
                <div class="pos north">
                    <div class="pos-label">N (${deal.north.hcp})</div>
                    <div class="cards">${formatHandForDisplay(deal.north.cards)}</div>
                </div>
                <div class="pos west">
                    <div class="pos-label">W (${deal.west.hcp})</div>
                    <div class="cards">${formatHandForDisplay(deal.west.cards)}</div>
                </div>
                <div class="pos east">
                    <div class="pos-label">E (${deal.east.hcp})</div>
                    <div class="cards">${formatHandForDisplay(deal.east.cards)}</div>
                </div>
                <div class="pos south">
                    <div class="pos-label">S (${deal.south.hcp})</div>
                    <div class="cards">${formatHandForDisplay(deal.south.cards)}</div>
                </div>
            </div>
        </div>\n`;
    });
    html += `    </div>\n`;
  });

  html += `</body>
</html>
`;

  return html;
};

const formatVulnShort = (vulnerability: string): string => {
  switch (vulnerability) {
    case 'Neither':
    case 'None':
      return 'None';
    case 'Both':
      return 'Both';
    case 'NS':
      return 'NS';
    case 'EW':
      return 'EW';
    default:
      return vulnerability;
  }
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatHandForDisplay = (cards: any[]): string => {
  const suits = ['S', 'H', 'D', 'C']; // Spades, Hearts, Diamonds, Clubs (standard diagram order)
  const suitSymbols = ['♠', '♥', '♦', '♣'];
  const redSuits = ['H', 'D'];
  let display = '';

  suits.forEach((suit, index) => {
    const suitCards = cards
      .filter(card => card.suit === suit)
      .sort((a, b) => getRankValue(b.rank) - getRankValue(a.rank))
      .map(card => card.rank)
      .join('');

    const colorClass = redSuits.includes(suit) ? 'red' : 'black';
    display += `<div class="suit-line ${colorClass}"><span class="suit-sym">${suitSymbols[index]}</span>${suitCards || '—'}</div>`;
  });

  return display;
};

// Download Functions
export const downloadFile = (content: string | Uint8Array, filename: string, mimeType: string): void => {
  const blob = content instanceof Uint8Array 
    ? new Blob([content], { type: mimeType })
    : new Blob([content], { type: mimeType });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}; 